import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, ArrowRight } from "lucide-react";

const DROPDOWNS = {
  jewellery: {
    to: "/jewellery",
    label: "Jewellery",
    links: [
      { to: "/jewellery/wholesale", label: "Wholesale" },
      { to: "/jewellery-manufacturer-meerut", label: "Manufacturing" },
      { to: "/jewellery/retail", label: "Retail" },
      { to: "/jewellery/rates", label: "Live Gold Rate" },
      { to: "/contact?type=wholesale_jewellery", label: "Jewellery Enquiry" },
    ],
  },
  realestate: {
    to: "/real-estate",
    label: "Real Estate",
    activeMatch: ["/real-estate", "/site-visit", "/landowner-partnerships"],
    links: [
      { to: "/real-estate", label: "RK Builders" },
      { to: "/projects", label: "Projects" },
      { to: "/contact#visit-us", label: "Locations" },
      { to: "/contact?type=property", label: "Property Enquiry" },
    ],
  },
};

const NAV = [
  { to: "/our-story", label: "Our Story" },
  { dropdown: "jewellery" },
  { dropdown: "realestate" },
  { to: "/projects", label: "Projects" },
  { to: "/leadership", label: "Leadership" },
  { to: "/contact", label: "Contact" },
];

const linkBase =
  "text-[11px] uppercase tracking-[0.24em] font-medium whitespace-nowrap transition-colors duration-300";
const linkTone = (active) =>
  active ? "text-[#C5A059]" : "text-[#FBF9F5]/60 hover:text-[#C5A059]";
const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");

function NavDropdown({ dd }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const matches = dd.activeMatch || [dd.to];
  const active = matches.some((m) => location.pathname.startsWith(m));

  return (
    <div
      className="relative h-full flex items-center"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <NavLink
        to={dd.to}
        data-testid={`nav-${slug(dd.label)}`}
        className={`${linkBase} ${linkTone(active)} flex items-center gap-1.5 py-2`}
        aria-expanded={open}
        aria-haspopup="true"
      >
        {dd.label}
        <ChevronDown
          size={11}
          className={`opacity-50 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </NavLink>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid={`dropdown-${slug(dd.label)}`}
            className="absolute left-0 top-full pt-2"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
          >
            <div className="w-[232px] bg-[#070C14]/[0.98] backdrop-blur-xl border border-white/[0.07] py-2 shadow-[0_20px_50px_rgba(0,0,0,0.5)]">
              {dd.links.map((l) => (
                <Link
                  key={l.label}
                  to={l.to}
                  data-testid={`dropdown-item-${slug(l.label)}`}
                  className="group flex items-center justify-between gap-4 px-5 py-3 text-[10.5px] uppercase tracking-[0.22em] font-medium text-[#FBF9F5]/65 hover:text-[#C5A059] transition-colors duration-200"
                >
                  {l.label}
                  <ArrowRight
                    size={11}
                    className="shrink-0 text-[#C5A059] opacity-0 -translate-x-1 transition-all duration-200 group-hover:opacity-100 group-hover:translate-x-0"
                  />
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [accordion, setAccordion] = useState(null);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const solid = scrolled || !isHome;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
          solid
            ? "bg-[#05080E]/[0.92] backdrop-blur-xl border-b border-white/[0.06]"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-6 sm:px-10 relative flex items-center justify-between h-20 lg:h-24">
          <Link to="/" data-testid="header-logo" className="flex items-center shrink-0">
            <img
              src="/images/rk-logo.png"
              alt="RK Group — Home"
              className="h-11 w-[46px] lg:h-12 lg:w-[51px] shrink-0 object-contain"
            />
          </Link>

          <nav
            className="hidden lg:flex items-center gap-11 absolute left-1/2 -translate-x-1/2 h-full"
            data-testid="header-nav"
          >
            {NAV.map((n) =>
              n.dropdown ? (
                <NavDropdown key={n.dropdown} dd={DROPDOWNS[n.dropdown]} />
              ) : (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${slug(n.label)}`}
                  className={({ isActive }) => `${linkBase} ${linkTone(isActive)} py-2`}
                >
                  {n.label}
                </NavLink>
              )
            )}
          </nav>

          <Link
            to="/contact"
            data-testid="header-enquire-btn"
            className="hidden lg:inline-block text-[11px] uppercase tracking-[0.24em] font-medium whitespace-nowrap text-[#C5A059] border border-[#C5A059]/40 px-8 py-3.5 hover:bg-[#C5A059] hover:text-[#05080E] transition-colors duration-300"
          >
            Enquire
          </Link>

          <button
            data-testid="mobile-menu-open-btn"
            className="lg:hidden text-[#FBF9F5] p-2"
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
          >
            <Menu size={22} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[70] bg-[#05080E] flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
          >
            <div className="flex items-center justify-between h-20 px-6 border-b border-white/[0.06]">
              <img src="/images/rk-logo.png" alt="RK Group" className="h-9 w-[38px] shrink-0 object-contain" />
              <button
                data-testid="mobile-menu-close-btn"
                onClick={() => setMenuOpen(false)}
                className="text-[#FBF9F5] p-2"
                aria-label="Close menu"
              >
                <X size={22} />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-7 py-8 flex flex-col">
              {NAV.map((n, i) => {
                const label = n.dropdown ? DROPDOWNS[n.dropdown].label : n.label;
                const s = slug(label);
                return (
                  <motion.div
                    key={label}
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 + i * 0.05 }}
                    className="border-b border-white/5"
                  >
                    {n.dropdown ? (
                      <>
                        <button
                          data-testid={`mobile-accordion-${s}`}
                          onClick={() => setAccordion(accordion === n.dropdown ? null : n.dropdown)}
                          aria-expanded={accordion === n.dropdown}
                          className="w-full flex items-center justify-between font-serif text-3xl py-4 text-[#FBF9F5]/85 hover:text-[#C5A059] transition-colors"
                        >
                          {label}
                          <ChevronDown
                            size={20}
                            className={`text-[#C5A059]/70 transition-transform duration-300 ${accordion === n.dropdown ? "rotate-180" : ""}`}
                          />
                        </button>
                        <AnimatePresence initial={false}>
                          {accordion === n.dropdown && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.35, ease: "easeInOut" }}
                              className="overflow-hidden"
                            >
                              <div className="pb-5 pl-4 flex flex-col gap-1">
                                <Link
                                  to={DROPDOWNS[n.dropdown].to}
                                  data-testid={`mobile-nav-${s}-overview`}
                                  className="text-[11px] uppercase tracking-[0.24em] text-[#C5A059] font-medium py-2.5"
                                >
                                  {label} Overview
                                </Link>
                                {DROPDOWNS[n.dropdown].links.map((c) => (
                                  <Link
                                    key={c.label}
                                    to={c.to}
                                    data-testid={`mobile-nav-sub-${slug(c.label)}`}
                                    className="text-[11px] uppercase tracking-[0.22em] text-[#94A3B8] hover:text-[#C5A059] transition-colors py-2.5"
                                  >
                                    {c.label}
                                  </Link>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </>
                    ) : (
                      <NavLink
                        to={n.to}
                        data-testid={`mobile-nav-${s}`}
                        className="block font-serif text-3xl py-4 text-[#FBF9F5]/85 hover:text-[#C5A059] transition-colors"
                      >
                        {label}
                      </NavLink>
                    )}
                  </motion.div>
                );
              })}
              <motion.div
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 + NAV.length * 0.05 }}
                className="border-b border-white/5"
              >
                <NavLink
                  to="/media"
                  data-testid="mobile-nav-media-updates"
                  className="block font-serif text-3xl py-4 text-[#FBF9F5]/85 hover:text-[#C5A059] transition-colors"
                >
                  Media &amp; Updates
                </NavLink>
              </motion.div>
            </nav>
            <div className="px-7 pb-10">
              <Link
                to="/contact"
                data-testid="mobile-enquire-btn"
                className="block w-full text-center text-[11px] uppercase tracking-[0.24em] font-medium text-[#C5A059] border border-[#C5A059]/40 px-4 py-4 hover:bg-[#C5A059] hover:text-[#05080E] transition-colors duration-300"
              >
                Enquire
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
